"use client";

import { useMemo, useState } from "react";
import Link from "@/lib/nav-link";
import { ArrowRight } from "lucide-react";

/**
 * "Is this price normal?" - the bridge from a search page to the
 * product, answered instantly from the page's own numbers.
 *
 * The comparison is the price per m² the visitor types against this
 * segment's distribution of real listings (p10 / p25 / ortalama / p75 /
 * p90, all sent with the page). No model call and no quota: it is a
 * position in a distribution, and it says so. The full valuation, with
 * condition, floor and the negotiation range, is one click away, which
 * is the point of the widget.
 */

export interface PriceCheckLabels {
  title: string;
  intro: string;
  price: string;
  area: string;
  low: string;
  below: string;
  fair: string;
  above: string;
  high: string;
  your: string;
  segment: string;
  cta: string;
  ctaHint: string;
  unit: string;
}

type Tone = "deal" | "fair" | "over";

function group(n: number, decimal: string): string {
  // Rent per m² is 11.4, not 11: keep one decimal below 100.
  if (Math.abs(n) < 100) return (Math.round(n * 10) / 10).toFixed(1).replace(/\.0$/, "").replace(".", decimal);
  return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

function parse(v: string): number | null {
  const n = Number(v.replace(/[\s ,]/g, ""));
  return Number.isFinite(n) && n > 0 ? n : null;
}

export default function PriceCheck({
  labels, p10, p25, p50, p75, p90, analyzeHref, defaultArea, pricePlaceholder = "250 000", decimal = ",",
}: {
  labels: PriceCheckLabels;
  p10: number | null;
  p25: number | null;
  p50: number | null;
  p75: number | null;
  p90: number | null;
  analyzeHref: string;
  defaultArea?: number | null;
  /** A typical price for this segment, so a rent page does not suggest 250 000. */
  pricePlaceholder?: string;
  decimal?: string;
}) {
  const [price, setPrice] = useState("");
  const [area, setArea] = useState(defaultArea ? String(defaultArea) : "");

  const result = useMemo((): { text: string; tone: Tone; ppm: number } | null => {
    const p = parse(price);
    const a = parse(area);
    if (!p || !a || !p50 || a < 10 || a > 1000) return null;
    const ppm = p / a;
    if (p10 && ppm < p10) return { text: labels.low, tone: "deal", ppm };
    if (p90 && ppm > p90) return { text: labels.high, tone: "over", ppm };
    if (p25 && ppm < p25) return { text: labels.below, tone: "deal", ppm };
    if (p75 && ppm > p75) return { text: labels.above, tone: "over", ppm };
    return { text: labels.fair, tone: "fair", ppm };
  }, [price, area, p10, p25, p50, p75, p90, labels]);

  const tone = result?.tone;
  const box =
    tone === "deal" ? "border-emerald-200 bg-emerald-50 text-emerald-900"
      : tone === "over" ? "border-rose-200 bg-rose-50 text-rose-900"
        : "border-amber-200 bg-amber-50 text-amber-900";

  const input = "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm tabular-nums text-gray-900 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100";

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
      <h2 className="text-lg font-bold tracking-tight text-gray-900">{labels.title}</h2>
      <p className="mt-0.5 text-sm text-gray-600">{labels.intro}</p>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <label className="block">
          <span className="text-xs font-semibold text-gray-500">{labels.price}</span>
          <input
            inputMode="numeric"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder={pricePlaceholder}
            className={`mt-1 ${input}`}
          />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-gray-500">{labels.area}</span>
          <input
            inputMode="numeric"
            value={area}
            onChange={(e) => setArea(e.target.value)}
            placeholder="85"
            className={`mt-1 ${input}`}
          />
        </label>
      </div>
      {result && (
        <div className={`mt-3 rounded-xl border px-3.5 py-3 ${box}`} aria-live="polite">
          <p className="text-sm font-semibold">{result.text}</p>
          <p className="mt-1 text-xs tabular-nums opacity-80">
            {labels.your.replace("{ppm}", `${group(result.ppm, decimal)} ${labels.unit}`)}
            {" · "}
            {labels.segment.replace("{ppm}", `${group(p50 ?? 0, decimal)} ${labels.unit}`)}
          </p>
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <Link
          href={analyzeHref}
          className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
        >
          {labels.cta}
          <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
        <span className="text-xs text-gray-500">{labels.ctaHint}</span>
      </div>
    </section>
  );
}
